import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  LogOut, RefreshCw, Search, Users, CreditCard, 
  CheckCircle, Phone, Mail 
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { DashboardStats } from '../components/admin/DashboardStats';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const HTTP_UNAUTHORIZED = 401;

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('bookings');
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('adminToken');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [statsRes, bookingsRes, paymentsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers: getAuthHeaders() }),
        axios.get(`${API}/admin/bookings`, { headers: getAuthHeaders() }),
        axios.get(`${API}/admin/payments`, { headers: getAuthHeaders() })
      ]);

      setStats(statsRes.data.data);
      setBookings(bookingsRes.data.data);
      setPayments(paymentsRes.data.data);
    } catch (err) {
      if (err.response?.status === HTTP_UNAUTHORIZED) {
        localStorage.removeItem('adminToken');
        navigate('/admin');
      } else {
        setError('Failed to load dashboard data. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin');
      return;
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    navigate('/admin');
  };

  const updateBookingStatus = async (bookingId, newStatus) => {
    try {
      await axios.put(
        `${API}/admin/bookings/${bookingId}/status`,
        { status: newStatus },
        { headers: getAuthHeaders() }
      );
      fetchData();
    } catch (err) {
      setError('Failed to update booking status. Please try again.');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      new: 'bg-blue-100 text-blue-700',
      contacted: 'bg-yellow-100 text-yellow-700',
      completed: 'bg-green-100 text-green-700',
      paid: 'bg-green-100 text-green-700',
      pending: 'bg-yellow-100 text-yellow-700',
      unpaid: 'bg-red-100 text-red-700'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100 text-gray-700'}`}>
        {status?.charAt(0).toUpperCase() + status?.slice(1) || 'Unknown'}
      </span>
    );
  };

  const filteredBookings = bookings.filter(b => 
    b.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    b.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    b.service?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredPayments = payments.filter(p =>
    p.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.customer_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.package_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center pt-24">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin text-red-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 pt-24 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Error Alert */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {error}
            <button 
              onClick={() => setError(null)} 
              className="ml-4 text-red-500 hover:text-red-700"
            >
              Dismiss
            </button>
          </div>
        )}
        
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
            <p className="text-gray-600">Manage bookings and payments</p>
          </div>
          <div className="flex gap-3">
            <Button onClick={fetchData} variant="outline" className="gap-2">
              <RefreshCw className="w-4 h-4" />
              Refresh
            </Button>
            <Button onClick={handleLogout} variant="outline" className="gap-2 text-red-600 border-red-600 hover:bg-red-50">
              <LogOut className="w-4 h-4" />
              Logout
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        {/* Stats Grid */}
        <DashboardStats stats={stats} />

        {/* Tabs */}
        <div className="flex gap-4 mb-6">
          <Button
            onClick={() => setActiveTab('bookings')}
            variant={activeTab === 'bookings' ? 'default' : 'outline'}
            className={activeTab === 'bookings' ? 'bg-red-600 hover:bg-red-700' : ''}
          >
            <Users className="w-4 h-4 mr-2" />
            Bookings ({bookings.length})
          </Button>
          <Button
            onClick={() => setActiveTab('payments')}
            variant={activeTab === 'payments' ? 'default' : 'outline'}
            className={activeTab === 'payments' ? 'bg-red-600 hover:bg-red-700' : ''}
          >
            <CreditCard className="w-4 h-4 mr-2" />
            Payments ({payments.length})
          </Button>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              placeholder="Search by name, email, or service..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* Content */}
        {activeTab === 'bookings' && (
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase">Service</th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {filteredBookings.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                          No bookings found
                        </td>
                      </tr>
                    ) : (
                      filteredBookings.map((booking) => (
                        <tr key={booking.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4">
                            <p className="font-medium text-gray-900">{booking.name}</p>
                            {booking.message && (
                              <p className="text-sm text-gray-500 truncate max-w-xs" title={booking.message}>
                                {booking.message}
                              </p>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex flex-col gap-1">
                              <a href={`mailto:${booking.email}`} className="text-sm text-blue-600 hover:underline flex items-center gap-1">
                                <Mail className="w-3 h-3" />
                                {booking.email}
                              </a>
                              <a href={`tel:${booking.phone}`} className="text-sm text-blue-600 hover:underline flex items-center gap-1">
                                <Phone className="w-3 h-3" />
                                {booking.phone}
                              </a>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <p className="text-sm text-gray-900">{booking.service}</p>
                          </td>
                          <td className="px-6 py-4">
                            <p className="text-sm text-gray-500">{formatDate(booking.created_at)}</p>
                          </td>
                          <td className="px-6 py-4">
                            {getStatusBadge(booking.status)}
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex gap-2">
                              {booking.status === 'new' && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => updateBookingStatus(booking.id, 'contacted')}
                                  className="text-yellow-600 border-yellow-600 hover:bg-yellow-50"
                                >
                                  Mark Contacted
                                </Button>
                              )}
                              {booking.status === 'contacted' && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => updateBookingStatus(booking.id, 'completed')}
                                  className="text-green-600 border-green-600 hover:bg-green-50"
                                >
                                  Mark Complete
                                </Button>
                              )}
                              {booking.status === 'completed' && (
                                <span className="text-green-600 text-sm flex items-center gap-1">
                                  <CheckCircle className="w-4 h-4" />
                                  Done
                                </span>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {activeTab === 'payments' && (
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase">Package</th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {filteredPayments.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                          No payments found
                        </td>
                      </tr>
                    ) : (
                      filteredPayments.map((payment) => (
                        <tr key={payment.session_id || payment._id} className="hover:bg-gray-50">
                          <td className="px-6 py-4">
                            <p className="font-medium text-gray-900">{payment.customer_name}</p>
                            <a href={`mailto:${payment.customer_email}`} className="text-sm text-blue-600 hover:underline">
                              {payment.customer_email}
                            </a>
                          </td>
                          <td className="px-6 py-4">
                            <p className="text-sm text-gray-900">{payment.package_name}</p>
                          </td>
                          <td className="px-6 py-4">
                            <p className="font-semibold text-gray-900">£{payment.amount?.toFixed(2)}</p>
                          </td>
                          <td className="px-6 py-4">
                            {getStatusBadge(payment.payment_status)}
                          </td>
                          <td className="px-6 py-4">
                            <p className="text-sm text-gray-500">{formatDate(payment.created_at)}</p>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
